/**
 * Icon — FlatIcon UIcons web-font wrapper.
 *
 * Mirrors the ROP-teams Icon primitive so the admin panel speaks the same
 * iconography. Stylesheets are imported once in main.tsx; this component
 * emits `<i class="fi fi-{prefix}-{name}">`.
 *
 * Family selection:
 *   regular -> fi-rr (default outline)
 *   solid   -> fi-sr (filled, for emphasis)
 *   thin    -> fi-tr (1px outline)
 *
 * Pass the icon's slug *without* the `fi-` prefix.
 */

import type { CSSProperties } from 'react';

export type IconWeight = 'regular' | 'solid' | 'thin';

export interface IconProps {
  name: string;
  weight?: IconWeight;
  size?: number;
  color?: string;
  className?: string;
  label?: string;
}

const PREFIX_BY_WEIGHT: Record<IconWeight, string> = {
  regular: 'fi-rr',
  solid: 'fi-sr',
  thin: 'fi-tr',
};

export function Icon({
  name,
  weight = 'regular',
  size,
  color,
  className,
  label,
}: IconProps): JSX.Element {
  const cls = `fi ${PREFIX_BY_WEIGHT[weight]}-${name}${className ? ` ${className}` : ''}`;
  const style: CSSProperties = { lineHeight: 1 };
  if (size !== undefined) style.fontSize = size;
  if (color !== undefined) style.color = color;
  if (label) return <i className={cls} style={style} role="img" aria-label={label} />;
  return <i className={cls} style={style} aria-hidden="true" />;
}
